import { api } from '../api.ts';
import { html, mount, type Raw } from '../html.ts';
import { formatDate, HEALTH, isOverdue, PROJECT_STATUS, t } from '../i18n.ts';
import { state } from '../state.ts';
import { emptyState, statusBadge } from '../ui.ts';
import { pageHeader, renderActivity } from './common.ts';

// Client portal: the start page for guests. One card per shared project with
// its current state; every section is loaded only when the project shares it
// (the server enforces the same rule through row-level security).

interface PortalProject {
  id: string;
  key: string;
  name: string;
  status: string;
  health: string | null;
  starts_on: string | null;
  ends_on: string | null;
  guest_sections: string[];
}

async function projectCard(project: PortalProject): Promise<Raw> {
  const sees = (section: string) => project.guest_sections.includes(section);
  const base = `/projects/${project.id}`;
  const [phases, gates, decisions, kpis] = await Promise.all([
    sees('phases') ? api.get<{ items: any[] }>(`${base}/phases`) : null,
    sees('gates') ? api.get<{ items: any[] }>(`${base}/gates`) : null,
    sees('decisions') ? api.get<{ items: any[] }>(`${base}/decisions?status=proposed`) : null,
    sees('kpis') ? api.get<{ items: any[] }>(`${base}/kpis`) : null,
  ]);
  const latest = kpis
    ? await Promise.all(
        kpis.items.slice(0, 4).map(async (k) => ({ ...k, last: (await api.get<{ items: any[] }>(`/kpis/${k.id}/measurements`)).items.at(-1) ?? null })),
      )
    : [];

  const facts: Raw[] = [];
  if (phases) {
    const index = phases.items.findIndex((p) => p.status === 'active');
    const current = phases.items[index];
    facts.push(html`<div><dt>${t('Aktuelle Phase')}</dt><dd>${
      current
        ? html`${current.name} <span class="pma-muted">· ${t('{n} von {total}', { n: index + 1, total: phases.items.length })}${current.ends_on ? ` · ${t('bis {date}', { date: formatDate(current.ends_on) })}` : ''}</span>`
        : '–'
    }</dd></div>`);
  }
  if (gates) {
    const next = gates.items.find((g) => g.status === 'pending');
    facts.push(html`<div><dt>${t('Nächster Meilenstein')}</dt><dd>${
      next
        ? html`${next.code} · ${next.name} <span class="${isOverdue(next.due_on) ? 'pma-overdue' : 'pma-muted'}">· ${formatDate(next.due_on)}</span>
            <div class="pma-muted pma-small">${t('{met} von {total} Kriterien erfüllt', { met: next.criteria.filter((c: any) => c.met === true).length, total: next.criteria.length })}</div>`
        : t('Alle Meilensteine entschieden')
    }</dd></div>`);
  }
  if (decisions) {
    facts.push(html`<div><dt>${t('Offene Entscheidungen')}</dt><dd>${
      decisions.items.length
        ? html`<ul class="pma-plain-list">${decisions.items.slice(0, 3).map((d) => html`<li>${d.title}</li>`)}</ul>${
            decisions.items.length > 3 ? html`<div class="pma-muted pma-small">${t('… und {n} weitere', { n: decisions.items.length - 3 })}</div>` : ''
          }`
        : t('Keine')
    }</dd></div>`);
  }
  if (latest.length) {
    facts.push(html`<div><dt>${t('Kennzahlen')}</dt><dd><ul class="pma-plain-list">${latest.map(
      (k) => html`<li>${k.name}: <strong>${k.last ? `${k.last.value}${k.unit ? ` ${k.unit}` : ''}` : '–'}</strong>${
        k.target !== null ? html` <span class="pma-muted">(${t('Ziel')} ${k.direction === 'higher' ? '≥' : '≤'} ${k.target})</span>` : ''
      }</li>`,
    )}</ul></dd></div>`);
  }

  return html`<section class="p-card" aria-labelledby="portal-${project.id}">
    <div class="p-card__header"><h2 id="portal-${project.id}">${project.name}</h2>
      <span>${statusBadge(project.status, PROJECT_STATUS)} ${project.health ? statusBadge(project.health, HEALTH) : ''}</span></div>
    <div class="p-card__body pma-stack">
      <p class="pma-muted pma-small">${project.key} · ${formatDate(project.starts_on)} – ${formatDate(project.ends_on)}</p>
      ${facts.length ? html`<dl class="pma-summary pma-summary--detail">${facts}</dl>` : ''}
      <h3 class="pma-small">${t('Letzte Aktivität')}</h3>
      <div data-portal-activity="${project.id}"></div>
      <p><a class="p-button p-button--sm" href="/projects/${project.id}">${t('Projekt öffnen')}</a></p>
    </div>
  </section>`;
}

export async function portalView(container: HTMLElement): Promise<void> {
  const { items } = await api.get<{ items: PortalProject[] }>('/projects');
  const firstName = state.me?.user.name.split(' ')[0] ?? '';
  const cards = await Promise.all(items.map(projectCard));
  mount(
    container,
    html`${pageHeader(t('Kundenportal'), firstName ? t('Guten Tag, {name}.', { name: firstName }) : t('Guten Tag.'), t('Der aktuelle Stand deiner Projekte – Phasen, Meilensteine, Entscheidungen und Kennzahlen.'))}
    ${
      cards.length
        ? html`<div class="pma-stack-lg">${cards}</div>`
        : emptyState(t('Noch kein Projekt freigegeben'), t('Sobald das Projektteam ein Projekt für dich freigibt, erscheint es hier.'))
    }`,
  );
  await Promise.all(
    items.map((p) => {
      const target = container.querySelector<HTMLElement>(`[data-portal-activity="${p.id}"]`);
      return target ? renderActivity(target, p.id, 5) : undefined;
    }),
  );
}
